import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import cn from 'classnames'
import { toast } from 'react-toastify'

import styles from './Summary.module.scss'
import { ReactComponent as ClearIcon } from '../../assets/clear-icon.svg'

import PeripheralBridge from '../../utils/peripheralBridge'
import useGetSummaryReport from '../../hooks/useGetSummaryReport'
import moment from 'moment'
import { map, startCase, isEqual } from 'lodash'
import { centsToDollars } from '../../utils/formatters'
import { orderSummaryKeys } from "../../../src/utils/orderUtils"
import Dialog from '../dialog/Dialog'
import { networkConnectedWithCallbacks, networkSignalSpeed } from '../../utils/networkConnected'
import SidebarModal from '../SidebarModal'

import { startSyncCycle } from '../../hooks/useSafMode'

import { APPLICATION_STRINGS } from '../../strings'

const OrderSummary = ({
  attendantName,
  allRevenueInCents,
  deviceUuid,
  className,
  menuName,
  isPaxDevice,
  offlineCardIds,
  offlineLocalIds,
  cardReaderFailedCardCount,
  unsyncableIds,
  onClose,
  onRequestSyncRefund,
  unsyncedRefundIds,
  venueName,
  safResponse,
  safIsUploading,
  setSafIsUploading,
  safUploadComplete,
}) => {
  const classNames = cn(styles.orderSummary, className)

  const offlineOrders = offlineLocalIds.length
  const offlineCards = offlineCardIds.length
  const unsyncableOrders = unsyncableIds.length
  const [getSummaryReport, { data: totals }] = useGetSummaryReport()

  const [showBridgeError, setShowBridgeError] = useState(false)
  const [syncRequestMade, setSyncRequestMade] = useState(false)
  const [stopSyncRequestMade, setStopSyncRequestMade] = useState(false)
  const [shouldSync, setShouldSync] = useState(false)
  const [signalSpeed, setSignalSpeed] = useState(4);
  const [insufficientNetwork, setInsufficientNetwork] = useState(false);

  const startSyncs = () => {
    setShouldSync(true)
  }

  const stopSyncs = () => {
    setShouldSync(false)
  }

  const networkFailure = () => {
    setInsufficientNetwork(true)
    toast.error('Sync Failed: Device is offline. Please check the network connection and try again.')
  }

  const onSyncClick = () => {
    setSyncRequestMade(false)
    networkConnectedWithCallbacks(startSyncs, networkFailure)
  }

  const onStopSyncClick = () => {
    stopSyncs()

    setStopSyncRequestMade(false)
    setSyncRequestMade(false)
    setSafIsUploading(false)
  }

  const syncRefunds = () => {
    unsyncedRefundIds.forEach(onRequestSyncRefund)
  }

  useEffect(() => {
    getSummaryReport()
    try {
      networkSignalSpeed(setSignalSpeed)
    } catch {
      setSignalSpeed(0)
    }
  }, [])

  useEffect(() => {
    if (!shouldSync) {
      return
    }

    const bridgeUnavailableCallback = () => {
      setShowBridgeError(true)
    }

    const getOfflineCardIds = () => {
      return offlineCardIds
    }

    const getOfflineLocalIds = () => {
      return offlineLocalIds
    }

    const getSafIsUploading = () => {
      return safIsUploading
    }

    const getSafUploadComplete = () => {
      return safUploadComplete
    }

    const bridgeInstance = PeripheralBridge.getBridge()
    startSyncCycle(false, getSafUploadComplete, getSafIsUploading, getOfflineCardIds, getOfflineLocalIds, bridgeUnavailableCallback, bridgeInstance)

    setShouldSync(false)
  }, [shouldSync])

  const renderFieldRow = (fields, index) => (
    <div
      className={styles.row}
      key={`${fields?.columnOne?.value}-${index}`}
    >
      {map(fields, (field) => (
        <div className={styles.column} style={field?.style}>
          {field?.value}
        </div>
      ))}
    </div>
  )

  const createField = (value = '', style = {}) => ({
    value,
    style,
  })

  const addWhiteTextStyle = {
    color: 'white',
    paddingTop: '16px'
  }

  const addBoldWhiteText = {
    color: 'white',
    fontWeight: '700',
    fontSize: '18px',
  }

  const sectionOneRows = [
    [
      createField('Venue: ' + venueName),
    ],
    [
      createField('Menu: ' + menuName),
    ],
    [
      createField('Date: ' + moment().format('DD/MM/YY HH:mm A')),
    ],
    [
      createField('Attendant: ' + attendantName),
    ],
    [
      createField('Device: ' + deviceUuid),
    ],
    [
      createField(),
    ],
  ]

  const sectionTwoRows = [
    [
      createField('Device Sync Status', addBoldWhiteText),
      createField('Qty', addBoldWhiteText)
    ],
    [
      createField('Unsynced Card Transactions', offlineCards > 0 ? {color: '#EB628D'} : {}),
      createField(offlineCards, {color: offlineCards > 0 ? '#EB628D' : 'white'})
    ],
    [
      createField('Failed Card Transaction Uploads', cardReaderFailedCardCount > 0 ? {color: '#EB628D'} : {}),
      createField(isPaxDevice ? cardReaderFailedCardCount : 'NA', {color: cardReaderFailedCardCount > 0 ? '#EB628D' : 'white'})
    ],
    [
      createField("Unsynced Order Details Data", offlineOrders > 0 ? {color: '#EB628D'} : {}),
      createField(offlineOrders ?? '0', {color: offlineOrders > 0 ? '#EB628D' : 'white'})
    ],
    [
      createField('Unsynced Refund Requests', totals?.pendingRefundCount > 0 ? {color: '#EB628D'} : {}),
      createField(totals?.pendingRefundCount ?? '0', {color: totals?.pendingRefundCount > 0 ? '#EB628D' : 'white'})
    ],
    [
      createField('Defective Orders Unable to Sync', unsyncableOrders > 0 ? {color: '#EB628D'} : {}),
      createField(unsyncableOrders ?? '0', {color: unsyncableOrders > 0 ? '#EB628D' : 'white'})
    ],

  ]

  const sectionThreeRows = [
    [
      createField("Today’s Synced Orders Totals", addBoldWhiteText),
      createField('Amount', addBoldWhiteText)
    ],
    [
      createField('Gross Sales'),
      createField(totals?.gross ?? centsToDollars(allRevenueInCents), addWhiteTextStyle),
    ],
    [
      createField('Discounts'),
      createField(totals?.discounts ?? '-', addWhiteTextStyle)
    ],
    [
      createField('Refunds'),
      createField(totals?.refunds ?? '-', addWhiteTextStyle)
    ],
    [
      createField('Net Sales'),
      createField(totals?.net ?? centsToDollars(allRevenueInCents), addWhiteTextStyle),
    ],
    [
      createField('Tips (excludes Mobile Wallet)'),
      createField(totals?.tips ?? '-', addWhiteTextStyle)
    ],

  ]

  const sectionFourDefaults = [
    [
      createField('Today’s Synced Orders Tenders', addBoldWhiteText),
      createField('Amount', addBoldWhiteText)
    ],
    [
      createField('Cash'),
      createField(totals?.cash ?? '$0.00', addWhiteTextStyle),
    ],
    [
      createField('Card'),
      createField(totals?.creditCard ?? '$0.00', addWhiteTextStyle)
    ],
    [
      createField('Mobile Wallet'),
      createField(totals?.virtualCurrency ?? '$0.00', addWhiteTextStyle)
    ]
  ]

  const syncMessage = "Please make sure you are on a stable network connection before continuing. If you continue, you will be blocked from any further actions on this device until the sync is complete. This process can take several minutes."
  const noSyncMessage = "All orders are synced."
  const stopSyncMessage = "Stopping the sync will leave some orders unsynced."
  const insufficientNetworkMessage = "Your network connection is insufficient.  Please find a stable network connection and retry syncing orders."

  const sectionFourRows = (() => {
      if (!totals || isEqual(Object.keys(totals), orderSummaryKeys)) {
        return sectionFourDefaults;
    } for (const key in totals) {
      if (orderSummaryKeys.includes(key)) {
        continue;
      }
      let rowEl = [
        createField(startCase(key)),
        createField(totals[key] ?? '-', addWhiteTextStyle)

      ];
      sectionFourDefaults.push(rowEl)
    }
    return sectionFourDefaults
  })()

  const sections = [sectionOneRows, sectionTwoRows, sectionThreeRows, sectionFourRows]
  const content = map(sections, (section) => {
    return (
      <>
        <div className={styles.borderRow}></div>
        {map(section, renderFieldRow)}

      </>
    )
  })

  return (
    <SidebarModal
      show
      title="Device Summary"
      onClose={onClose}
    >
      <div className={classNames}>
        {!safResponse?.success && (offlineCards > 0 || offlineOrders > 0) && 
          (
            <div className={styles.syncDetailsContainer}>
              <div>
                <div>{APPLICATION_STRINGS.SAF_ERROR}</div>
              </div>
            </div>
          )
        }
        
        {content}
        {showBridgeError && (
          <div className={styles.syncDetailsContainer}>
            <div>No card reader connection. Try syncing again.</div>
          </div>
        )}
        {safIsUploading && (
          <div className={styles.syncButtonContainer}>
            <button className={styles.stopSyncButton} onClick={() => setStopSyncRequestMade(true)}>
              Stop Syncing
            </button>
          </div>
        )}
        {(offlineCards > 0 || offlineOrders > 0) && (
            <div className={styles.syncDetailsContainer}>
                <span>Find a stable network connection to sync</span>
            </div>
        )}
        {!safIsUploading && (
          <div className={styles.syncButtonContainer}>
            <button className={styles.syncButton} onClick={() => setSyncRequestMade(true)}>
              Sync Orders
            </button>
            <button className={styles.syncButton} onClick={syncRefunds}>
              Sync Refunds
            </button>
          </div>
        )}
        {stopSyncRequestMade && (
          <Dialog message={stopSyncMessage}>
            <div className={styles.actionsContainer}>
              <button className={styles.buttonContinue} onClick={() => setStopSyncRequestMade(false)}>Continue Sync</button>
              <button className={styles.buttonCancel} onClick={onStopSyncClick}>Stop Sync</button>
            </div>
          </Dialog>
        )}
        {syncRequestMade && (offlineCards > 0 || offlineOrders > 0) && (
          <Dialog message={syncMessage}>
            <div className={styles.actionsContainer}>
              <button className={styles.buttonCancel} onClick={() => setSyncRequestMade(false)}>Cancel Sync</button>
              <button className={styles.buttonContinue} onClick={onSyncClick}>Continue</button>
            </div>
          </Dialog>
        )}
        {syncRequestMade && (offlineCards === 0 && offlineOrders === 0) && (
          <Dialog message={noSyncMessage}>
            <div className={styles.actionsContainer}>
              <button className={styles.buttonContinue} onClick={() => setSyncRequestMade(false)}>OK</button>
            </div>
          </Dialog>
        )}
        {insufficientNetwork && (
          <Dialog message={insufficientNetworkMessage}>
            <div className={styles.actionsContainer}>
              <button className={styles.buttonContinue} onClick={() => setInsufficientNetwork(false)}>OK</button>
            </div>
          </Dialog>
        )}
      </div>
    </SidebarModal>
  )
}

OrderSummary.defaultProps = {
  allRevenueInCents: 0,
  menuName: '',
  offlineCardIds: [],
  offlineLocalIds: [],
  isPaxDevice: true,
  cardReaderFailedCardCount: 0,
  unsyncableIds: [],
  orderCounts: {},
}

OrderSummary.propTypes = {
  allRevenueInCents: PropTypes.number,
  menuName: PropTypes.string,
  offlineCardIds: PropTypes.array,
  offlineLocalIds: PropTypes.array,
  isPaxDevice: PropTypes.bool,
  cardReaderFailedCardCount: PropTypes.number,
  unsyncableIds: PropTypes.array,
  onInitializeSync: PropTypes.func,
  orderCounts: PropTypes.object,
  updateSafIsUploading: PropTypes.func,
}

export default OrderSummary
